import { useEditor, useNode } from "@craftjs/core";
import React, { useContext } from "react";

import {
  UnitInput,
  PaddingInput,
  Tabs,
  MarginInput,
  Panel,
  ToggleInput,
  RadioButtonInput,
  Spacer,
  TabsV2,
  RangeNumberInput,
  ColorInput,
  SelectInput,
} from "../../input";
import { isUpdatedFromDefault } from "../../utils";

import { supportLibrary } from "../../block-supports";
import { GrigoraDashboardContext } from "../../dashboard/context";

export const DropdownSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    query: { node },
    actions: { setProp: setPropEditor },
  } = useEditor();

  const {
    width = "200px",
    widthTablet,
    widthMobile,
    height,
    heightTablet,
    heightMobile,
    margin,
    marginTablet,
    marginMobile,
    padding,
    paddingTablet,
    paddingMobile,
    toggle = "hide",
    openListOnHover,
    color,
    activeColor,
    backgroundColor,
    activeBackgroundColor,
    transition,
    position,
  } = props;

  const { blockState, setBlockState } =
    useContext(GrigoraDashboardContext) ?? {};

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing={"1rem"}>
          <RadioButtonInput
            isResetVisible={false}
            resetValue={"hide"}
            value={blockState?.(id)?.["is-opened"] ? "show" : "hide"}
            onChange={(e) => {
              setBlockState(id, "is-opened", e === "show" ? true : false);
            }}
            options={[
              {
                value: "show",
                label: "Show",
              },
              {
                value: "hide",
                label: "Hide",
              },
            ]}
          />
          <SelectInput
            resetValue={undefined}
            value={position}
            onChange={(val) => {
              if (val === "left" || val === "right") {
                const dropdownListNodeId =
                  node(id).get().data.linkedNodes[`dropdown-list-${id}`];
                setPropEditor(dropdownListNodeId, (props) => {
                  props.width = "300px";
                  props.minWidth = "100%";
                  props.maxWidth = "100%";
                });
              }
              setProp((props) => {
                props.position = val;
              });
            }}
            options={[
              { label: "Bottom", value: "bottom" },
              { label: "Top", value: "top" },
              { label: "Left", value: "left" },
              { label: "Right", value: "right" },
            ]}
            label="Position"
          />
          <ToggleInput
            resetValue={undefined}
            value={openListOnHover}
            onChange={(val) => {
              setProp((props) => {
                props.openListOnHover = val;
              });
            }}
            label={"Open list on hover"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={"200px"}
            value={width}
            valueMobile={widthMobile}
            valueTablet={widthTablet}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.widthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.widthMobile = val;
              });
            }}
            label={"Width"}
          />

          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            value={height}
            valueMobile={heightMobile}
            valueTablet={heightTablet}
            onChange={(val) => {
              setProp((props) => {
                props.height = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.heightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.heightMobile = val;
              });
            }}
            label={"Height"}
          />
        </Spacer>
      </div>
    );
  }

  function StyleSettings() {
    const colorsTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalColorSettings(),
      },
      {
        label: "Active",
        key: "active",
        children: activeColorSettings(),
      },
    ];
    function normalColorSettings() {
      return (
        <>
          <ColorInput
            resetValue={undefined}
            value={color}
            onChange={(val) => {
              setProp((props) => {
                props.color = val;
              });
            }}
            label={"Color"}
          />
          <ColorInput
            value={backgroundColor}
            resetValue={undefined}
            onChange={(val) => {
              setProp((props) => {
                props.backgroundColor = val;
              });
            }}
            label={"Background"}
          />
        </>
      );
    }

    function activeColorSettings() {
      return (
        <>
          <ColorInput
            resetValue={undefined}
            value={activeColor}
            onChange={(val) => {
              setProp((props) => {
                props.activeColor = val;
              });
            }}
            label={"Color"}
          />
          <ColorInput
            value={activeBackgroundColor}
            resetValue={undefined}
            onChange={(val) => {
              setProp((props) => {
                props.activeBackgroundColor = val;
              });
            }}
            label={"Background"}
          />{" "}
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition}
            onChange={(val) => {
              setProp((props) => {
                props.transition = val;
              });
            }}
            label={"Transition Time"}
          />
        </>
      );
    }

    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: color },
              { key: activeColor },
              { key: backgroundColor },
              { key: activeBackgroundColor },
            ])}
            id={`block-setting-${id}-dropdown-colors`}
            title={"Colors"}
            initialOpen={true}
          >
            <TabsV2 items={colorsTabs} />
          </Panel>
        </div>
      </>
    );
  }
  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: margin },
            { key: marginTablet },
            { key: marginMobile },
            { key: padding },
            { key: paddingTablet },
            { key: paddingMobile },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={false}
        >
          <MarginInput
            label={"Margin"}
            isResponsive={true}
            valueTablet={marginTablet}
            valueMobile={marginMobile}
            value={margin}
            onChange={(val) => {
              setProp((props) => {
                props.margin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.marginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.marginMobile = val;
              });
            }}
          />
          <PaddingInput
            label={"Padding"}
            isResponsive={true}
            valueTablet={paddingTablet}
            valueMobile={paddingMobile}
            value={padding}
            onChange={(val) => {
              setProp((props) => {
                props.padding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.paddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.paddingMobile = val;
              });
            }}
          />
        </Panel>

        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: width },
        { key: widthTablet },
        { key: widthMobile },
        { key: height },
        { key: heightTablet },
        { key: heightMobile },
      ]),
    },

    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: color },
        { key: backgroundColor },
        { key: activeColor },
        { key: activeBackgroundColor },
      ]),
    },

    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: margin },
        { key: marginTablet },
        { key: marginMobile },
        { key: padding },
        { key: paddingTablet },
        { key: paddingMobile },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        key={`block-setting-tab-${id}`}
        id={`block-settings-tab-${id}`}
        type="card"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
